import { useEffect, useMemo, useRef, useState, type JSX } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Pencil, Plus, RotateCcw, Unplug, UserPlus, Users, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { RowActions } from '@/components/ui/row-actions';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Pill } from '@/components/ui/pill';
import { Tag } from '@/components/ui/tag';
import { Combobox } from '@/components/ui/combobox';
import { FilterField } from '@/components/ui/filter-field';
import { EmptyState } from '@/components/ui/empty-state';
import { Pagination } from '@/components/ui/pagination';
import {
  DataTable,
  DataTableCell,
  DataTableHeader,
  DataTableHeaderCell,
  DataTableRow,
  DataTableSkeleton,
} from '@/components/ui/data-table';
import { PageHeader } from '@/components/ui/page-header';
import {
  useAdminTeams,
  useAdminAccessRequests,
  useApproveAccessRequest,
  useCreateInviter,
  useLinkAccessRequest,
  useRejectAccessRequest,
  useUpdateInviter,
  useDeactivateInviter,
  useResetPortalLink,
  useAdminInvitersPaged,
  useAllAdminInviters,
  type OutreachTeam,
} from '@/hooks/useAdmin';
import { useUrlState } from '@/hooks/useUrlState';
import { useMainScopeLabel, useTeamScope } from '@/hooks/useTeamScope';
import { useDebounce } from '@/hooks/useDebounce';
import {
  PAY_MODEL_LABELS,
  accessRequestStatusLabel,
  payModelLabel,
  teamLabel,
} from '@/lib/labels';
import { formatDate } from '@/lib/dates';
import { formatMoney } from '@/lib/money';
import type {
  CompensationModel,
  OutreachAccessRequest,
  OutreachInviter,
} from '@/api/types';

const PAGE_SIZE = 50;

interface FormValues {
  name: string;
  team: string;
  compensationModel: CompensationModel;
  retainerAmount: string;
  email: string;
  paypalEmail: string;
  clippingId: string;
  notes: string;
}

const EMPTY: FormValues = {
  name: '',
  team: '',
  compensationModel: 'PER_CONVERSION',
  retainerAmount: '',
  email: '',
  paypalEmail: '',
  clippingId: '',
  notes: '',
};

const PAY_MODEL_OPTIONS = (Object.keys(PAY_MODEL_LABELS) as CompensationModel[]).map((m) => ({
  value: m,
  label: PAY_MODEL_LABELS[m] ?? m,
}));

/** Active teams for pickers, plus `keep` when it's an inactive team already on the row. */
function teamOptions(teams: OutreachTeam[], keep?: string): { value: string; label: string }[] {
  const options = teams
    .filter((t) => t.isActive || t.code === keep)
    .map((t) => ({ value: t.code, label: t.isActive ? t.displayName : `${t.displayName} (inactive)` }));
  // An outreacher on a team that's no longer in the list still shows it.
  if (keep && !options.some((o) => o.value === keep)) options.push({ value: keep, label: teamLabel(keep) });
  return options;
}

function payModelText(inv: Pick<OutreachInviter, 'compensationModel' | 'retainerAmount'>): string {
  const label = payModelLabel(inv.compensationModel);
  return inv.compensationModel === 'RETAINER' && inv.retainerAmount
    ? `${label} · $${formatMoney(inv.retainerAmount)}/week`
    : label;
}

export function AdminInviters(): JSX.Element {
  const { team, setTeam, locked, params: teamParams } = useTeamScope();
  const mainScopeLabel = useMainScopeLabel();
  const [f, setF] = useUrlState({
    q: '',
    page: 1,
    inactive: false as boolean,
    view: '',
    access: 'pending',
    edit: '',
  });

  // Search is typed locally and pushed to the URL once it settles.
  const [searchInput, setSearchInput] = useState(f.q);
  const debouncedSearch = useDebounce(searchInput, 300);
  useEffect(() => {
    if (debouncedSearch.trim() !== f.q) setF({ q: debouncedSearch.trim(), page: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);
  useEffect(() => {
    // Back/forward or a link changed the URL: follow it.
    setSearchInput((cur) => (cur.trim() === f.q ? cur : f.q));
  }, [f.q]);

  const inviters = useAdminInvitersPaged({
    ...teamParams,
    isActive: f.inactive ? undefined : true,
    search: f.q || undefined,
    page: f.page,
    limit: PAGE_SIZE,
  });
  const teams = useAdminTeams();
  const create = useCreateInviter();
  const update = useUpdateInviter();
  const deactivate = useDeactivateInviter();
  const resetLink = useResetPortalLink();

  const [editing, setEditing] = useState<OutreachInviter | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'disconnect' | 'deactivate';
    inviter: OutreachInviter;
  } | null>(null);
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);
  const formRef = useRef<HTMLDivElement>(null);

  const form = useForm<FormValues>({ defaultValues: EMPTY });

  const teamByCode = useMemo(
    () => new Map((teams.data ?? []).map((t) => [t.code, t])),
    [teams.data],
  );
  const nameOfTeam = (code: string): string => teamLabel(code, teamByCode.get(code)?.displayName);

  const rows = inviters.data?.data ?? [];
  const total = inviters.data?.meta.total ?? 0;

  function openCreate(): void {
    setEditing(null);
    form.reset({ ...EMPTY, team: team || '' });
    setShowForm(true);
    requestAnimationFrame(() => formRef.current?.scrollIntoView({ block: 'nearest' }));
  }
  function openEdit(inviter: OutreachInviter): void {
    setEditing(inviter);
    form.reset({
      name: inviter.name,
      team: inviter.team,
      compensationModel: inviter.compensationModel,
      retainerAmount: inviter.retainerAmount ?? '',
      email: inviter.email ?? '',
      paypalEmail: inviter.paypalEmail ?? '',
      clippingId: inviter.clippingId ?? '',
      notes: inviter.notes ?? '',
    });
    setShowForm(true);
    requestAnimationFrame(() => formRef.current?.scrollIntoView({ block: 'nearest' }));
  }
  function closeForm(): void {
    setShowForm(false);
    setEditing(null);
    if (f.edit) setF({ edit: '' });
  }

  // `?edit=<publicId>` (from the profile page's Edit link) opens the form once
  // the row is on screen.
  useEffect(() => {
    if (!f.edit || showForm) return;
    const row = rows.find((r) => r.publicId === f.edit);
    if (row) openEdit(row);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [f.edit, rows]);

  async function onSubmit(values: FormValues): Promise<void> {
    if (!values.team) {
      form.setError('team', { type: 'required' });
      return;
    }
    const payload: Partial<OutreachInviter> = {
      name: values.name.trim(),
      // Teams are CRUDable; the generated type still lists the original three.
      team: values.team as OutreachInviter['team'],
      compensationModel: values.compensationModel,
      retainerAmount:
        values.compensationModel === 'RETAINER' && values.retainerAmount
          ? values.retainerAmount
          : null,
      email: values.email || null,
      paypalEmail: values.paypalEmail || null,
      clippingId: values.clippingId || null,
      notes: values.notes || null,
    };
    try {
      if (editing) {
        await update.mutateAsync({ id: editing.id, body: payload });
        toast.success(`Saved ${payload.name}`);
      } else {
        await create.mutateAsync(payload);
        toast.success(`Added ${payload.name}`);
      }
      closeForm();
    } catch (err) {
      toast.error((err as Error).message);
    }
  }

  function reactivate(inv: OutreachInviter): void {
    update.mutate(
      { id: inv.id, body: { isActive: true } },
      {
        onSuccess: () => toast.success(`${inv.name} is active again`),
        onError: (err: Error) => toast.error(err.message),
      },
    );
  }

  const formTeam = form.watch('team');
  const formPayModel = form.watch('compensationModel');

  return (
    <div className="space-y-6">
      <PageHeader
        title="Outreachers"
        description="The people who message creators, and how they're paid."
        actions={
          <Button onClick={openCreate}>
            <Plus className="h-4 w-4" /> Add outreacher
          </Button>
        }
      />

      <AccessRequests
        focus={f.view === 'access'}
        mode={f.access === 'all' ? 'all' : 'pending'}
        onModeChange={(m) => setF({ access: m })}
        teams={teams.data ?? []}
      />

      {showForm && (
        <div ref={formRef}>
          <Card>
            <CardHeader>
              <CardTitle>{editing ? `Edit ${editing.name}` : 'New outreacher'}</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={form.handleSubmit(onSubmit)} className="grid gap-3" noValidate>
                <div className="grid gap-1">
                  <Label htmlFor="name">Name</Label>
                  <Input id="name" {...form.register('name', { required: true })} />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1">
                    <Label>Team</Label>
                    <Combobox
                      value={formTeam}
                      onChange={(v) => {
                        form.setValue('team', v, { shouldDirty: true });
                        form.clearErrors('team');
                      }}
                      options={teamOptions(teams.data ?? [], editing?.team)}
                      loading={teams.isLoading}
                      placeholder="Pick a team"
                    />
                    {form.formState.errors.team && (
                      <p className="text-xs text-destructive">Pick a team.</p>
                    )}
                  </div>
                  <div className="grid gap-1">
                    <Label>Pay model</Label>
                    <Combobox
                      value={formPayModel}
                      onChange={(v) =>
                        form.setValue('compensationModel', v as CompensationModel, {
                          shouldDirty: true,
                        })
                      }
                      options={PAY_MODEL_OPTIONS}
                      placeholder="Pick a pay model"
                    />
                  </div>
                </div>
                {formPayModel === 'RETAINER' && (
                  <div className="grid gap-1">
                    <Label htmlFor="retainerAmount">Retainer per week ($)</Label>
                    <Input
                      id="retainerAmount"
                      inputMode="decimal"
                      {...form.register('retainerAmount')}
                    />
                  </div>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="grid gap-1">
                    <Label htmlFor="email">Email</Label>
                    <Input id="email" type="email" {...form.register('email')} />
                  </div>
                  <div className="grid gap-1">
                    <Label htmlFor="paypalEmail">PayPal email</Label>
                    <Input id="paypalEmail" type="email" {...form.register('paypalEmail')} />
                  </div>
                </div>
                <div className="grid gap-1">
                  <Label htmlFor="clippingId">Clipping user ID</Label>
                  <Input
                    id="clippingId"
                    aria-describedby="clippingId-hint"
                    {...form.register('clippingId')}
                  />
                  <p id="clippingId-hint" className="text-xs text-muted-foreground">
                    Required if this person is paid through Clipping.
                  </p>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="outline" onClick={closeForm}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={create.isPending || update.isPending}>
                    {editing ? 'Save' : 'Add outreacher'}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3">
        <FilterField label="Search">
          <Input
            className="w-full md:w-64"
            type="search"
            aria-label="Search outreachers by name or email"
            placeholder="Name or email"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
        </FilterField>
        {!locked && (
          <FilterField label="Team">
            <Combobox
              className="w-full md:w-44"
              value={team}
              onChange={setTeam}
              options={(teams.data ?? [])
                .filter((t) => t.isActive)
                .map((t) => ({ value: t.code, label: t.displayName }))}
              allowAll
              allLabel={mainScopeLabel}
              loading={teams.isLoading}
            />
          </FilterField>
        )}
        <label className="flex h-9 items-center gap-2 text-sm pointer-coarse:h-11">
          <input
            type="checkbox"
            className="h-4 w-4 accent-[var(--primary)]"
            checked={f.inactive}
            onChange={(e) => setF({ inactive: e.target.checked, page: 1 })}
          />
          Include inactive
        </label>
      </div>

      {inviters.error ? (
        <p role="alert" className="text-sm text-destructive">
          Couldn't load outreachers: {(inviters.error as Error).message}
        </p>
      ) : (
        <DataTable aria-label="Outreachers" aria-busy={inviters.isFetching}>
          <DataTableHeader>
            <tr>
              <DataTableHeaderCell>Name</DataTableHeaderCell>
              <DataTableHeaderCell>Team</DataTableHeaderCell>
              <DataTableHeaderCell>Pay model</DataTableHeaderCell>
              <DataTableHeaderCell>Email</DataTableHeaderCell>
              <DataTableHeaderCell>Login</DataTableHeaderCell>
              {f.inactive && <DataTableHeaderCell>Status</DataTableHeaderCell>}
              <DataTableHeaderCell>
                <span className="sr-only">Actions</span>
              </DataTableHeaderCell>
            </tr>
          </DataTableHeader>
          {inviters.isLoading ? (
            <DataTableSkeleton cols={['w-32', 'w-20', 'w-24', 'w-40', 'w-24', 'w-32']} />
          ) : (
            <tbody>
              {rows.map((inv) => (
                <DataTableRow
                  key={inv.id}
                  state={inv.isActive ? 'default' : 'terminal'}
                  className="cursor-pointer"
                  onClick={(e) => {
                    // The row opens the profile; its own controls (and the
                    // portalled menu) keep their clicks.
                    if ((e.target as HTMLElement).closest('a,button,input,[role="menuitem"],[role="dialog"],[role="alertdialog"]')) return;
                    navigate(`/admin/outreachers/${inv.publicId}`);
                  }}
                >
                  <DataTableCell anchor>
                    <div className="flex items-center gap-2">
                      <Users className="h-3.5 w-3.5 shrink-0 text-muted-foreground" aria-hidden />
                      <Link
                        to={`/admin/outreachers/${inv.publicId}`}
                        className="hover:underline"
                      >
                        {inv.name}
                      </Link>
                    </div>
                  </DataTableCell>
                  <DataTableCell data-label="Team">
                    <Tag>{nameOfTeam(inv.team)}</Tag>
                  </DataTableCell>
                  <DataTableCell data-label="Pay model">{payModelText(inv)}</DataTableCell>
                  <DataTableCell muted data-label="Email">
                    {inv.email ?? '—'}
                  </DataTableCell>
                  <DataTableCell data-label="Login">
                    <Pill tone={inv.clerkUserId ? 'paid' : 'muted'}>
                      {inv.clerkUserId ? 'Connected' : 'Not signed up'}
                    </Pill>
                  </DataTableCell>
                  {f.inactive && (
                    <DataTableCell data-label="Status">
                      <Pill tone={inv.isActive ? 'paid' : 'muted'}>
                        {inv.isActive ? 'Active' : 'Inactive'}
                      </Pill>
                    </DataTableCell>
                  )}
                  <DataTableCell actions>
                    <div className="flex justify-end">
                      <RowActions
                        triggerLabel={`Actions for ${inv.name}`}
                        items={[
                          { label: 'Edit details', icon: Pencil, onClick: () => openEdit(inv) },
                          !!inv.clerkUserId && {
                            label: 'Disconnect login',
                            icon: Unplug,
                            onClick: () => setConfirming({ kind: 'disconnect', inviter: inv }),
                          },
                          inv.isActive
                            ? {
                                label: 'Deactivate',
                                icon: XCircle,
                                destructive: true,
                                onClick: () => setConfirming({ kind: 'deactivate', inviter: inv }),
                              }
                            : {
                                label: 'Reactivate',
                                icon: RotateCcw,
                                disabled: update.isPending,
                                onClick: () => reactivate(inv),
                              },
                        ]}
                      />
                    </div>
                  </DataTableCell>
                </DataTableRow>
              ))}
            </tbody>
          )}
        </DataTable>
      )}

      {/* Controlled, outside the table: a menu item unmounts as soon as it's
          picked, so the dialog can't live inside it. */}
      <ConfirmDialog
        open={confirming?.kind === 'disconnect'}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={`Disconnect ${confirming?.inviter.name ?? ''}'s login?`}
        description={
          <>
            This signs them out of the outreacher portal. Their invites, earnings and payouts are
            untouched. Next time they sign in, their login is linked to this outreacher again.
          </>
        }
        confirmLabel="Disconnect login"
        pending={resetLink.isPending}
        onConfirm={() => {
          const inv = confirming?.inviter;
          if (!inv) return;
          resetLink.mutate(inv.id, {
            onSuccess: () => toast.success(`${inv.name}'s login disconnected`),
            onError: (err: Error) => toast.error(err.message),
          });
        }}
      />
      <ConfirmDialog
        open={confirming?.kind === 'deactivate'}
        onOpenChange={(open) => !open && setConfirming(null)}
        title={`Deactivate ${confirming?.inviter.name ?? ''}?`}
        description={
          <>
            They lose access to the portal and stop appearing on Payroll, so pay anything they're
            owed first. Their invites and past payouts stay. You can reactivate them any time.
          </>
        }
        tone="destructive"
        confirmLabel="Deactivate"
        pending={deactivate.isPending}
        onConfirm={() => {
          const inv = confirming?.inviter;
          if (!inv) return;
          deactivate.mutate(inv.id, {
            onSuccess: () => toast.success(`${inv.name} deactivated`),
            onError: (err: Error) => toast.error(err.message),
          });
        }}
      />

      {!inviters.isLoading && !inviters.error && rows.length === 0 && (
        <EmptyState
          icon={<Users className="h-6 w-6" />}
          title={f.q ? `No outreachers match "${f.q}".` : 'No outreachers here yet.'}
          hint={
            f.inactive
              ? 'Try another team or clear the search.'
              : 'Inactive outreachers are hidden. Tick "Include inactive" to see them.'
          }
        />
      )}

      <Pagination
        page={f.page}
        limit={PAGE_SIZE}
        total={total}
        onPageChange={(page) => setF({ page })}
      />
    </div>
  );
}

// ── Access requests ─────────────────────────────────────────────────────

type OpenMode = 'approve' | 'link';

function AccessRequests({
  focus,
  mode,
  onModeChange,
  teams,
}: {
  focus: boolean;
  mode: 'pending' | 'all';
  onModeChange: (m: 'pending' | 'all') => void;
  teams: OutreachTeam[];
}): JSX.Element | null {
  const query = useAdminAccessRequests(mode === 'all' ? 'ALL' : 'PENDING');
  const pendingCount = useAdminAccessRequests('PENDING');
  const approve = useApproveAccessRequest();
  const reject = useRejectAccessRequest();
  const link = useLinkAccessRequest();
  const [open, setOpen] = useState<{ id: number; mode: OpenMode } | null>(null);
  const headingRef = useRef<HTMLHeadingElement>(null);

  const rows = query.data?.data ?? [];
  const pending = pendingCount.data?.meta.total ?? pendingCount.data?.data.length ?? 0;

  // Linking needs the full unclaimed list, so only load it once a Link form opens.
  const unclaimed = useAllAdminInviters({ isActive: true }, { enabled: open?.mode === 'link' });
  const unclaimedInviters = (unclaimed.data ?? []).filter((i) => !i.clerkUserId);

  // Home links here with ?view=access: bring the section into view and focus
  // it, once, after the first load.
  const focused = useRef(false);
  useEffect(() => {
    if (!focus || focused.current || query.isLoading) return;
    focused.current = true;
    headingRef.current?.scrollIntoView({ block: 'start' });
    headingRef.current?.focus({ preventScroll: true });
  }, [focus, query.isLoading]);

  const teamName = (code: string | null): string =>
    code ? teamLabel(code, teams.find((t) => t.code === code)?.displayName) : 'any team';

  // Nothing waiting and nobody asked to see this: keep the page quiet.
  if (!focus && mode === 'pending' && !query.isLoading && rows.length === 0) return null;

  return (
    <section aria-labelledby="access-requests-h" className="space-y-3">
      <Card>
        <CardHeader className="flex flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>
            <h2
              id="access-requests-h"
              ref={headingRef}
              tabIndex={-1}
              className="flex items-center gap-2 outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <UserPlus className="h-4 w-4 text-muted-foreground" aria-hidden />
              Access requests
              <span className="text-xs font-normal text-muted-foreground">
                {pending} awaiting approval
              </span>
            </h2>
          </CardTitle>
          <div role="group" aria-label="Which requests to show" className="flex gap-1">
            {(['pending', 'all'] as const).map((m) => (
              <Button
                key={m}
                size="sm"
                variant={mode === m ? 'default' : 'outline'}
                aria-pressed={mode === m}
                onClick={() => onModeChange(m)}
              >
                {m === 'pending' ? 'Awaiting approval' : 'All'}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          {query.isLoading ? (
            <p className="text-sm text-muted-foreground">Loading requests…</p>
          ) : query.error ? (
            <p role="alert" className="text-sm text-destructive">
              Couldn't load access requests: {(query.error as Error).message}
            </p>
          ) : rows.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {mode === 'pending' ? 'No one is waiting for approval.' : 'No access requests yet.'}
            </p>
          ) : (
            rows.map((r) => (
              <AccessRequestRow
                key={r.id}
                request={r}
                teams={teams}
                requestedTeamName={teamName(r.requestedTeam)}
                openMode={open?.id === r.id ? open.mode : null}
                onSetMode={(m) => setOpen(m ? { id: r.id, mode: m } : null)}
                unclaimedInviters={unclaimedInviters}
                unclaimedLoading={unclaimed.isLoading}
                onApprove={(body) =>
                  approve.mutate(
                    { id: r.id, body },
                    {
                      onSuccess: (res) => {
                        setOpen(null);
                        toast.success(`Approved — ${res.inviter.name} added to ${teamName(body.team)}`);
                      },
                      onError: (err: Error) => toast.error(err.message),
                    },
                  )
                }
                approving={approve.isPending}
                onLink={(inviterId) =>
                  link.mutate(
                    { id: r.id, inviterId },
                    {
                      onSuccess: (res) => {
                        setOpen(null);
                        toast.success(`Connected to ${res.inviter.name}`);
                      },
                      onError: (err: Error) => toast.error(err.message),
                    },
                  )
                }
                linking={link.isPending}
                onReject={(reason) =>
                  reject.mutate(
                    { id: r.id, reason },
                    {
                      onSuccess: () => toast.success('Request declined'),
                      onError: (err: Error) => toast.error(err.message),
                    },
                  )
                }
                rejecting={reject.isPending}
              />
            ))
          )}
        </CardContent>
      </Card>
    </section>
  );
}

function AccessRequestRow({
  request,
  teams,
  requestedTeamName,
  openMode,
  onSetMode,
  unclaimedInviters,
  unclaimedLoading,
  onApprove,
  approving,
  onLink,
  linking,
  onReject,
  rejecting,
}: {
  request: OutreachAccessRequest;
  teams: OutreachTeam[];
  requestedTeamName: string;
  openMode: OpenMode | null;
  onSetMode: (mode: OpenMode | null) => void;
  unclaimedInviters: OutreachInviter[];
  unclaimedLoading: boolean;
  onApprove: (body: { team: string; compensationModel: CompensationModel; name: string }) => void;
  approving: boolean;
  onLink: (inviterId: number) => void;
  linking: boolean;
  onReject: (reason: string) => void;
  rejecting: boolean;
}): JSX.Element {
  const requestedIsActive = teams.some((t) => t.isActive && t.code === request.requestedTeam);
  const [team, setTeam] = useState(requestedIsActive ? (request.requestedTeam ?? '') : '');
  const [compensationModel, setCompensationModel] = useState<CompensationModel>('PER_CONVERSION');
  const [name, setName] = useState(request.name);
  const [linkInviterId, setLinkInviterId] = useState<string>('');

  // Teams can arrive after the row mounts; pre-select the requested team then.
  useEffect(() => {
    if (!team && requestedIsActive && request.requestedTeam) setTeam(request.requestedTeam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestedIsActive]);

  const busy = approving || linking || rejecting;
  const isPending = request.status === 'PENDING';

  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <div className="flex flex-wrap items-baseline gap-2">
            <span className="font-medium">{request.name}</span>
            <span className="text-xs text-muted-foreground">{request.email}</span>
            {!isPending && (
              <Pill tone={request.status === 'APPROVED' ? 'paid' : 'rejected'}>
                {accessRequestStatusLabel(request.status)}
              </Pill>
            )}
          </div>
          <p className="whitespace-pre-wrap text-sm text-foreground/90">{request.note}</p>
          <p className="text-xs text-muted-foreground">
            Asked to join {requestedTeamName} · {formatDate(request.createdAt)}
            {!isPending && request.reviewedAt && ` · Reviewed ${formatDate(request.reviewedAt)}`}
          </p>
          {request.status === 'REJECTED' && request.rejectionReason && (
            <p className="text-xs text-muted-foreground">Reason: {request.rejectionReason}</p>
          )}
        </div>
        {isPending && (
          <div className="flex flex-wrap gap-1">
            <Button
              size="sm"
              variant={openMode === 'link' ? 'default' : 'outline'}
              onClick={() => onSetMode(openMode === 'link' ? null : 'link')}
              disabled={busy}
              title="Connect this person's login to an outreacher you already added"
            >
              {openMode === 'link' ? 'Cancel' : 'Link existing'}
            </Button>
            <Button
              size="sm"
              variant={openMode === 'approve' ? 'default' : 'outline'}
              onClick={() => onSetMode(openMode === 'approve' ? null : 'approve')}
              disabled={busy}
              title="Add a new outreacher from this request"
            >
              {openMode === 'approve' ? 'Cancel' : 'Create new'}
            </Button>
            <ConfirmDialog
              title={`Decline ${request.name}'s access request?`}
              description={
                <>
                  They see the reason below. They can send a new request later. No outreacher is
                  added.
                </>
              }
              tone="destructive"
              confirmLabel="Decline request"
              reasonRequired
              reasonLabel="Reason (shown to them)"
              reasonPlaceholder="Plain language — they'll read this as written."
              pending={rejecting}
              onConfirm={(reason) => onReject(reason)}
              trigger={
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-destructive hover:text-destructive"
                  disabled={busy}
                >
                  <XCircle className="h-3 w-3" /> Decline
                </Button>
              }
            />
          </div>
        )}
      </div>

      {isPending && openMode === 'approve' && (
        <div className="mt-3 grid gap-3 border-t pt-3 sm:grid-cols-3">
          <div className="grid gap-1">
            <Label htmlFor={`ar-name-${request.id}`}>Name</Label>
            <Input id={`ar-name-${request.id}`} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="grid gap-1">
            <Label>Team</Label>
            <Combobox
              value={team}
              onChange={setTeam}
              options={teamOptions(teams)}
              placeholder="Pick a team"
            />
          </div>
          <div className="grid gap-1">
            <Label>Pay model</Label>
            <Combobox
              value={compensationModel}
              onChange={(v) => setCompensationModel(v as CompensationModel)}
              options={PAY_MODEL_OPTIONS}
              placeholder="Pick a pay model"
            />
          </div>
          <div className="flex justify-end sm:col-span-3">
            <Button
              size="sm"
              onClick={() => onApprove({ team, compensationModel, name: name.trim() })}
              disabled={approving || !team || !name.trim()}
            >
              Approve and add outreacher
            </Button>
          </div>
        </div>
      )}

      {isPending && openMode === 'link' && (
        <div className="mt-3 grid gap-3 border-t pt-3">
          <div className="grid gap-1">
            <Label>Outreacher to connect</Label>
            <Combobox
              value={linkInviterId}
              onChange={setLinkInviterId}
              loading={unclaimedLoading}
              options={unclaimedInviters.map((i) => ({
                value: String(i.id),
                label: `${i.name}${i.email ? ` · ${i.email}` : ''}`,
                hint: teamLabel(i.team, teams.find((t) => t.code === i.team)?.displayName),
              }))}
              placeholder={
                !unclaimedLoading && unclaimedInviters.length === 0
                  ? 'Every active outreacher already has a login'
                  : 'Search by name or email'
              }
            />
            <p className="text-xs text-muted-foreground">
              Only outreachers whose login isn't connected yet appear here. Linking connects this
              person's sign-in to that outreacher, so they land in the portal next time they sign
              in.
            </p>
          </div>
          <div className="flex justify-end">
            <Button
              size="sm"
              onClick={() => onLink(Number.parseInt(linkInviterId, 10))}
              disabled={
                linking || !linkInviterId || !Number.isFinite(Number.parseInt(linkInviterId, 10))
              }
            >
              Connect login
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
